import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCollection } from "@/lib/hooks/useCollection";
import { formatDate } from "@/lib/format";
import { institutionState, type InstitutionState } from "./zone";
import type { Country, Institution, LicenceTier } from "@/types";

/**
 * Every institution.
 *
 * The zone column is present while there is one zone, for the same reason the
 * badge is: the day there are two, this has to already be a column people read
 * rather than a new one appearing in a table they thought they knew.
 */

const STATE_TONE: Record<InstitutionState, string> = {
  active: "text-cue-green",
  expiring: "text-cue-amber",
  lapsed: "text-accent",
  suspended: "text-accent",
};

export function InstitutionsPage() {
  const t = useT();
  const { locale } = useI18n();
  const { rows, loading } = useCollection<Institution>("institutions");

  const [state, setState] = useState<InstitutionState | "all">("all");
  const [country, setCountry] = useState<Country | "all">("all");
  const [tier, setTier] = useState<LicenceTier | "all">("all");

  const now = Date.now();
  const shown = useMemo(() => {
    const collator = new Intl.Collator(locale === "de" ? "de-DE" : "en-GB");
    return rows
      .filter((institution) => {
        if (state !== "all" && institutionState(institution, now) !== state) return false;
        if (country !== "all" && institution.country !== country) return false;
        if (tier !== "all" && institution.licenceTier !== tier) return false;
        return true;
      })
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [rows, state, country, tier, locale, now]);

  const countries = useMemo(
    () => [...new Set(rows.map((institution) => institution.country))].sort(),
    [rows]
  );

  return (
    <SectionShell
      titleKey="institutions.title"
      actions={
        <Link
          to="/institutions/new"
          className="inline-flex items-center rounded-md bg-cue-green px-4 py-2
                     text-sm font-medium text-white hover:bg-cue-green/90"
        >
          {t("institutions.new")}
        </Link>
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <label>
          <span className="field-label">{t("institutions.filter.status")}</span>
          <select
            className="field-input"
            value={state}
            onChange={(event) => setState(event.target.value as InstitutionState | "all")}
          >
            <option value="all">{t("institutions.filter.all")}</option>
            <option value="active">{t("institutions.state.active")}</option>
            <option value="expiring">{t("institutions.state.expiring")}</option>
            <option value="lapsed">{t("institutions.state.lapsed")}</option>
            <option value="suspended">{t("institutions.state.suspended")}</option>
          </select>
        </label>

        <label>
          <span className="field-label">{t("institutions.filter.country")}</span>
          <select
            className="field-input"
            value={country}
            onChange={(event) => setCountry(event.target.value as Country | "all")}
          >
            <option value="all">{t("institutions.filter.all")}</option>
            {countries.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="field-label">{t("institutions.filter.tier")}</span>
          <select
            className="field-input"
            value={tier}
            onChange={(event) => setTier(event.target.value as LicenceTier | "all")}
          >
            <option value="all">{t("institutions.filter.all")}</option>
            {(["solo", "small", "medium", "large"] as const).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-6">
        {loading ? (
          <p className="py-10 text-center text-sm text-ink-muted">{t("common.loading")}</p>
        ) : shown.length === 0 ? (
          <EmptyState titleKey="institutions.empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("institutions.title")}</caption>
              <thead>
                <tr className="border-b border-paper-deep text-left text-ink-muted">
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.name")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.country")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.zone")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.segment")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.tier")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.seats")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.validUntil")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("institutions.column.status")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((institution) => {
                  const current = institutionState(institution, now);
                  return (
                    <tr key={institution.id} className="border-b border-paper-warm last:border-0">
                      <td className="px-3 py-3">
                        <Link
                          to={`/institutions/${institution.id}`}
                          className="font-medium text-ink underline-offset-2 hover:underline"
                        >
                          {institution.name}
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-ink-muted">{institution.country}</td>
                      <td className="px-3 py-3 text-ink-muted">{t(`zone.${institution.zone}`)}</td>
                      <td className="px-3 py-3 text-ink-muted">
                        {t(`institutions.segment.${institution.segment}`)}
                      </td>
                      <td className="px-3 py-3 text-ink-muted">{institution.licenceTier}</td>
                      <td className="px-3 py-3 text-ink-muted">{institution.teacherSeats}</td>
                      <td className="px-3 py-3 text-ink-muted">
                        {formatDate(locale, institution.validUntil)}
                      </td>
                      <td className={`px-3 py-3 ${STATE_TONE[current]}`}>
                        {t(`institutions.state.${current}`)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionShell>
  );
}
